import { phone, cep, cpf } from "src/framework/commons/masks";
import { PROPOSTA_OPTIONS, TIPO_FATURAMENTO, TIPOS_SOLUCAO_OPTIONS } from "../constants";
import { ListagemItem, ModuloPorRegiaoListagemResponse, VinculoModuloResponse, EstruturaListagemResponse, EstruturaListagem } from "../types";
import { ProjetoComponente, ProjetoOutput } from "./types";
import { Conta, Estado, Regiao } from "src/types";
import { cnpj } from "src/framework/commons/masks";
import { getLatLng } from "react-places-autocomplete";
import { ID_CONTROLADOR, ID_INVERSOR_CARREGADOR, ID_MODULO } from "src/components/componentes/types";
import { safeGeocodeByAddress } from "src/framework/commons/MapsUtil";
import { ProjetoPersonalizadoOffgridData } from "src/store/vendas/projetos/personalizado_offgrid/personalizadoOffgrid.types";
import { DEFAULT_PROJETO_PERSONALIZADO_OFFGRID } from "src/store/vendas/projetos/personalizado_offgrid/personalizadoOffgrid.constants";
import { ComponenteProvider } from "src/providers/estoque/ComponenteProvider";
import { buscarEstruturasPorModulo } from "../personalizados_offgrid/utils/buscarEstruturasPorModulo";
import { buscarAcessoriosPorGrupo } from "../personalizados_offgrid/utils/buscarAcessoriosPorGrupo";

interface EstruturasOutput {
  ativos: EstruturaListagemResponse[];
  todos: EstruturaListagemResponse[];
  listagem: EstruturaListagem[];
}

interface Props {
  estados: Estado[]
  regioes: any[];
  estadosServico: any[];
  clonarProjeto: boolean;
}

const componenteProvider = new ComponenteProvider()

export interface ProjetoOutputPersonalizadoOff extends Omit<ProjetoOutput, "componentes"> {
  componentes: ProjetoComponente[]
}

export class ProjetoHelperPersonalizadoOff {
  public moduloSelecionado: ProjetoOutputPersonalizadoOff["componentes"][0] | null = null;
  public controladores: ProjetoComponente[] = [];
  public carregadores: ProjetoComponente[] = [];
  public acessorios: ProjetoComponente[] = [];
  public regioesProjeto: number[] = [];
  public estruturasListagem: EstruturasOutput = {
    ativos: [],
    todos: [],
    listagem: [],
  }
  public modulosListagem: ModuloPorRegiaoListagemResponse[] = [];
  public controladoresListagem: VinculoModuloResponse[] = [];
  public carregadoresListagem: VinculoModuloResponse[] = [];
  public acessoriosCalculados: ListagemItem[] = [];
  public estruturasCalculadas: ListagemItem[] = [];
  public acessoriosListagem: ListagemItem[] = [];
  public valorMinimoServico: { valor_minimo_servico: number } | null = null;
  
  constructor(private data: ProjetoOutputPersonalizadoOff, private readonly props: Props){
    this.data = this.normalizar()
    this.definirModuloSelecionado();
    this.definirControladores();
    this.definirCarregadores();
  }
  
  definirModuloSelecionado = () => {
    if(this.data?.componentes?.length){
      const moduloSelecionado = this.data?.componentes?.find(item => item.componente.categoria_componentes_id === ID_MODULO) || null;
      if (!moduloSelecionado) {
        console.error("Modulo selecionado não encontrado")
      }
  
      this.moduloSelecionado = moduloSelecionado;
    }
  }

  definirControladores = () => {
    this.controladores = this.data.componentes?.filter(item => item?.componente?.categoria_componentes_id === ID_CONTROLADOR) || [];
  }

  definirCarregadores = () => {
    this.carregadores = this.data.componentes?.filter(item => item?.componente?.categoria_componentes_id === ID_INVERSOR_CARREGADOR) || [];
  }

  buscarModulos = async (regioes: number[]) => {
    const result = await componenteProvider.buscarModulosPorRegiao(
      regioes?.map(String), 
      this.data.potencia || 0,
      undefined,
      undefined,
      undefined,
      undefined,
      "off",
    )

    this.modulosListagem = result
  }

  buscarControladores = async (regioes: number[]) => {
    const result = await componenteProvider.buscarVinculosModuloId(this.moduloSelecionado?.componente.id || 0, {
      categorias: [ID_CONTROLADOR],
      regiao_id: regioes,
      segmento: 'off'
    })

    this.controladoresListagem = result
  }

  buscarCarregadores = async (regioes: number[]) => {
    const result = await componenteProvider.buscarVinculosModuloId(this.moduloSelecionado?.componente.id || 0, {
      categorias: [ID_INVERSOR_CARREGADOR],
      regiao_id: regioes,
      segmento: 'off'
    })

    this.carregadoresListagem = result
  }

  buscarEstruturas = async (regioes: number[]) => {
    const result = await buscarEstruturasPorModulo({
      moduloId: this.moduloSelecionado?.componente.id || 0,
      potencia: this.data.potencia || 0,
      regioesId: regioes || [],
    })

    this.estruturasListagem = result || [];
  }
  
  buscarAcessorios = async (regioes: number[]) => {
    const result = await buscarAcessoriosPorGrupo({
      regioesId: regioes || [],
      segmento: 'off'
    })

    this.acessoriosListagem = result || [];
  }
  
  getLatLngFromAddress = async (): Promise<{ lat: number; lng: number }> => {
    const { logradouro, numero, bairro, cep }  = this.data.endereco;
    const { cidade, estado_id } = this.data
    const endereco = `${logradouro || ''}, ${numero || ''}, ${bairro || ''}, ${cidade?.nome || ''}, ${estado_id}, ${cep}`
    
    try {
      const res = await safeGeocodeByAddress(endereco)
      if (!res?.[0]) return { lat: 0, lng: 0 }
      return getLatLng(res[0])
    } catch (error) {
      return {
        lat: 0,
        lng: 0,
      }
    }
  }

  normalizar = (): ProjetoOutputPersonalizadoOff => {
    const maskedPhone = phone({ target: { value: this.data.telefone_proposta || '' } })?.target?.value || ''
    const maskedCep = cep({ target: { value: this.data.endereco?.cep || '' } })?.target?.value || ''
    const maskedCpfCnpj = this.formatarCpfOuCnpj(this.data.cpf_cnpj_proposta)

    return {
      ...this.data,
      telefone_proposta: maskedPhone,
      cpf_cnpj_proposta: maskedCpfCnpj,
      endereco: {
        ...(this.data.endereco || {}),
        cep: maskedCep,
      },
    };
  }

  formatarCpfOuCnpj = (value: string) => {
    if (!value) return '';
    const formatValue = value?.replace(/[^a-zA-Z0-9]/g, '')
    return formatValue.length === 14 ? cnpj({ target: { value: formatValue } })?.target?.value || '' : cpf({ target: { value: formatValue } })?.target?.value || ''
  }

  buscarEstado = (): { id: string; nome: string } | undefined => {
    if (!this.data.estado_id) return undefined;
    const estado = this.props.estados?.find((item) => item.id === this.data.estado_id)
    return estado ? { id: estado.id, nome: estado.nome } : undefined
  }

  mostrarOpcaoServico = () => {
    if (!this.data?.id) return true;
    const reg = this.props.regioes || []
    const estadosServico = this.props.estadosServico || []
    const estadosPermitemServico: string[] = estadosServico.map((item: any) => item.regiao_id)
    const currentRegioes: string[] = reg.filter((item: Regiao) => {
      if (!item.estados || item.estados.length === 0) return false;
      const ids = item.estados.map((estado: any) => estado.estado_id)
      return ids.includes(this.data.estado_id)
    }).map((item: any) => item.id)
    return estadosPermitemServico.some(item => {
      if (currentRegioes.includes(item)) return true;
      return false;
    })
  }

  getRegioes = () => {
    if (!this.data.estado_id) return []
    return this.props.regioes?.filter((regiao: any) => {
      const estadosList = regiao.estados || []

      const estados = estadosList.map((item: any) => item.estado_id)
      return estados.includes(this.data.estado_id)
    }).map(item => item.id)
  }

  getTipoSolucao = () => {
    const controlador = this.data.componentes?.find(item => item.componente.categoria_componentes_id === ID_CONTROLADOR);
    return TIPOS_SOLUCAO_OPTIONS.find(item => item.value === (!!controlador ? ID_CONTROLADOR : ID_INVERSOR_CARREGADOR));
  }

  formatar = async (): Promise<ProjetoPersonalizadoOffgridData> => {
    const estadoProjeto = this.buscarEstado();
    const regioesProejto = this.getRegioes();

    await Promise.all([
      this.buscarModulos(regioesProejto),
      this.buscarControladores(regioesProejto),
      this.buscarCarregadores(regioesProejto),
      this.buscarEstruturas(regioesProejto),
      this.buscarAcessorios(regioesProejto),
      // this.calcularValorServico(),
    ])

    const latLngFromAddress = await this.getLatLngFromAddress()
    const tipoProposta = PROPOSTA_OPTIONS.find(item => item.value === (!!this.data.valor_servico ? 'gerador-com-instalacao' : 'gerador-sem-instalacao')) || null;
    const tipoSolucao = this.getTipoSolucao();

    return {
      ...DEFAULT_PROJETO_PERSONALIZADO_OFFGRID,
      id: this.data.id,
      acessorios: [],
      tipo: "personalizado",
      tipo_proposta: tipoProposta,
      tipo_solucao: tipoSolucao,
      titulo: this.data.titulo,
      valor: this.data.valor || 0,
      valor_servico: this.data.valor_servico || 0,
      valor_servico_pis_cofins: this.data.valor_servico_pis_cofins || 0,
      codigo_servico: this.data.codigo_servico,
      estado: estadoProjeto,
      estado_original: estadoProjeto,
      estado_id: this.data.estado_id,
      potencia: this.data.potencia || 0,
      ev: (this.data.ev as unknown) as Conta,
      ev_id: this.data.ev?.id,
      revenda: (this.data.revenda as unknown) as Conta,
      justificativa_valor_servico: this.data.justificativa_valor_servico || null,
      revenda_id: this.data.revenda?.id,
      regioes: regioesProejto,
      latitude: latLngFromAddress.lat,
      longitude: latLngFromAddress.lng,
      analista: (this.data.analista as unknown) as Conta,
      analista_id: this.data.analista_id,
      assistente_id: this.data.assistente_id,
      bairro: this.data.endereco?.bairro || '',
      cep: this.data.endereco.cep,
      cep_encontrado: !!this.data.endereco.cep,
      cidade: this.data.cidade ? { estado_id: this.data.cidade.estado_id, cidade_codigo: this.data.cidade.cidade_codigo, id: this.data.cidade.id, nome: this.data.cidade.nome } : null,
      cidade_id: this.data.cidade?.id,
      cliente_proposta: this.data.cliente_proposta,
      clonar_projeto: this.props.clonarProjeto,
      codigo_venda: this.data.codigo_venda,
      cpf_cnpj_proposta: this.data.cpf_cnpj_proposta,
      distribuidor: this.data.distribuidor ? {
        cnpj_cpf: this.data.distribuidor.cnpj_cpf,
        fantasia: this.data.distribuidor.fantasia,
        id: this.data.distribuidor.id,
        nome: this.data.distribuidor.nome
      } : null,
      distribuidor_id: this.data.distribuidor?.id,
      email_proposta: this.data.email_proposta,
      endereco_proposta: this.data.endereco?.logradouro || '',
      estruturasSelecionadas: [],
      isEdit: true,
      modulosList: [],
      numero: this.data.endereco?.numero || '',
      recebe_servico: this.mostrarOpcaoServico(),
      resultado_calculos: null,
      segmento: "distribuicao_offgrid",
      servico: this.data.servico,
      tipo_faturamento: this.data.tipo_faturamento || TIPO_FATURAMENTO[0].value || null,
      supervisor_id: this.data.supervisor_id,
      telefone_proposta: this.data.telefone_proposta,
      old_componentes_edicao: [],
      old_estado_id: ''
    }
  }
}